// Datum und Zeitzone ohne Bibliothek — läuft unverändert in Deno (Edge Functions) und im Browser.
//
// Begriffe:
//   * „Instant"  = absoluter Zeitpunkt (ISO-String mit Z bzw. Date).
//   * „Kalendertag" = "YYYY-MM-DD" in der Zeitzone des Mandanten. Alle Tagesgrenzen (Mitternacht,
//     15:00-Erinnerung, Monatsabschluss) sind Kalendertage in DIESER Zone, nicht in UTC und nicht
//     in der Zone des Servers. Deshalb gibt es Tage mit 23 und 25 Stunden (Sommer-/Winterzeit).

export type LocalDate = string; // YYYY-MM-DD

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

const formatterCache = new Map<string, Intl.DateTimeFormat>();

function formatter(timeZone: string): Intl.DateTimeFormat {
  let f = formatterCache.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    formatterCache.set(timeZone, f);
  }
  return f;
}

export interface LocalParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

export function localParts(instant: Date | string, timeZone: string): LocalParts {
  const date = typeof instant === "string" ? new Date(instant) : instant;
  const parts: Record<string, number> = {};
  for (const p of formatter(timeZone).formatToParts(date)) {
    if (p.type !== "literal") parts[p.type] = Number(p.value);
  }
  return {
    year: parts.year!,
    month: parts.month!,
    day: parts.day!,
    hour: parts.hour!,
    minute: parts.minute!,
    second: parts.second!,
  };
}

export function localDateOf(instant: Date | string, timeZone: string): LocalDate {
  const p = localParts(instant, timeZone);
  return `${p.year}-${pad(p.month)}-${pad(p.day)}`;
}

/** "HH:MM" der Ortszeit. */
export function localTimeOf(instant: Date | string, timeZone: string): string {
  const p = localParts(instant, timeZone);
  return `${pad(p.hour)}:${pad(p.minute)}`;
}

/** Abstand Ortszeit − UTC in Millisekunden zu einem Instant. */
function offsetMs(instant: Date, timeZone: string): number {
  const p = localParts(instant, timeZone);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return asUtc - Math.floor(instant.getTime() / 1000) * 1000;
}

/**
 * Ortszeit (Kalendertag + "HH:MM") → Instant.
 *
 * Bei der Zeitumstellung gibt es Ortszeiten zweimal (Herbst) oder gar nicht (Frühjahr). Hier gilt:
 * doppelte Zeit ⇒ die frühere, fehlende Zeit ⇒ um die Lücke nach vorn verschoben. Für unsere Grenzen
 * (00:00, 15:00) spielt das in Europa nie eine Rolle, weil dort um 02:00/03:00 umgestellt wird.
 */
export function zonedTimeToInstant(date: LocalDate, time: string, timeZone: string): Date {
  const [y, m, d] = parseLocalDate(date);
  const [hh, mm] = time.split(":").map(Number) as [number, number];
  const naive = Date.UTC(y, m - 1, d, hh, mm, 0);
  const first = naive - offsetMs(new Date(naive), timeZone);
  const second = naive - offsetMs(new Date(first), timeZone);
  return new Date(Math.min(first, second));
}

/** [Beginn, Ende) eines Kalendertags als Instants. */
export function dayBounds(date: LocalDate, timeZone: string): { start: Date; end: Date } {
  return {
    start: zonedTimeToInstant(date, "00:00", timeZone),
    end: zonedTimeToInstant(addDays(date, 1), "00:00", timeZone),
  };
}

export function parseLocalDate(date: LocalDate): [number, number, number] {
  const match = DATE_RE.exec(date);
  if (!match) throw new Error(`Ungültiges Datum: ${date}`);
  return [Number(match[1]), Number(match[2]), Number(match[3])];
}

export function isLocalDate(value: unknown): value is LocalDate {
  if (typeof value !== "string" || !DATE_RE.test(value)) return false;
  const [y, m, d] = parseLocalDate(value);
  const probe = new Date(Date.UTC(y, m - 1, d));
  return probe.getUTCFullYear() === y && probe.getUTCMonth() === m - 1 && probe.getUTCDate() === d;
}

export function addDays(date: LocalDate, days: number): LocalDate {
  const [y, m, d] = parseLocalDate(date);
  const next = new Date(Date.UTC(y, m - 1, d + days));
  return toLocalDate(next.getUTCFullYear(), next.getUTCMonth() + 1, next.getUTCDate());
}

export function toLocalDate(year: number, month: number, day: number): LocalDate {
  return `${year}-${pad(month)}-${pad(day)}`;
}

/** 0 = Montag … 6 = Sonntag (passt zu schedule_templates.weekday_minutes). */
export function weekdayIndex(date: LocalDate): number {
  const [y, m, d] = parseLocalDate(date);
  return (new Date(Date.UTC(y, m - 1, d)).getUTCDay() + 6) % 7;
}

export function eachDay(from: LocalDate, to: LocalDate): LocalDate[] {
  const days: LocalDate[] = [];
  for (let day = from; day <= to; day = addDays(day, 1)) days.push(day);
  return days;
}

export function monthStart(date: LocalDate): LocalDate {
  return `${date.slice(0, 7)}-01`;
}

export function monthEnd(date: LocalDate): LocalDate {
  const [y, m] = parseLocalDate(monthStart(date));
  const last = new Date(Date.UTC(y, m, 0));
  return toLocalDate(y, m, last.getUTCDate());
}

/** Minuten seit Mitternacht für "HH:MM". */
export function clockMinutes(time: string): number {
  const [h, m] = time.split(":").map(Number) as [number, number];
  return h * 60 + m;
}

function pad(n: number): string {
  return String(n).padStart(2, "0");
}
